import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Star, 
  Menu, 
  X, 
  ChevronRight,
  Instagram,
  Facebook,
  ArrowRight,
  Check,
  Users,
  Award,
  Calendar as CalendarIcon,
  Heart
} from "lucide-react";

// ==================== SCROLL TO TOP ====================
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== NAVBAR ====================
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isHome = location.pathname === "/";
  const navBg = isScrolled || !isHome ? "bg-white shadow-sm" : "bg-transparent";
  const textColor = isScrolled || !isHome ? "text-[#1A1A1A]" : "text-white";

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${navBg}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="nav-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/b1ln6q90_Logo%20vivah%20tents03-01-11-34-07.jpg" 
                alt="Vivah Tents Logo" 
                className="h-28 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-10">
              <Link to="/" className={`nav-link ${textColor}`} data-testid="nav-home">Home</Link>
              <Link to="/about" className={`nav-link ${textColor}`} data-testid="nav-about">About</Link>
              <Link to="/services" className={`nav-link ${textColor}`} data-testid="nav-services">Services</Link>
              <Link to="/gallery" className={`nav-link ${textColor}`} data-testid="nav-gallery">Gallery</Link>
              <Link to="/reviews" className={`nav-link ${textColor}`} data-testid="nav-reviews">Reviews</Link>
              <Link to="/quote" data-testid="nav-quote">
                <Button className="bg-[#C5A059] hover:bg-[#B08D45] text-white rounded-none px-6 py-2 uppercase tracking-widest text-xs">
                  Get Quote
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className={`md:hidden ${textColor}`}
              onClick={() => setIsMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <button 
            className="absolute top-6 right-6 text-white"
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid="mobile-menu-close"
          >
            <X size={28} />
          </button>
          <Link to="/" className="mobile-menu-link" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
          <Link to="/about" className="mobile-menu-link" onClick={() => setIsMobileMenuOpen(false)}>About</Link>
          <Link to="/services" className="mobile-menu-link" onClick={() => setIsMobileMenuOpen(false)}>Services</Link>
          <Link to="/gallery" className="mobile-menu-link" onClick={() => setIsMobileMenuOpen(false)}>Gallery</Link>
          <Link to="/reviews" className="mobile-menu-link" onClick={() => setIsMobileMenuOpen(false)}>Reviews</Link>
          <Link to="/quote" className="mobile-menu-link" onClick={() => setIsMobileMenuOpen(false)}>Get Quote</Link>
          <div className="mt-8 flex gap-6">
            <a href="https://www.instagram.com/vivahtents/" target="_blank" rel="noopener noreferrer" className="text-[#C5A059]">
              <Instagram size={24} />
            </a>
            <a href="https://www.facebook.com/p/Vivah-Tents-GTA-100089740652389/" target="_blank" rel="noopener noreferrer" className="text-[#C5A059]">
              <Facebook size={24} />
            </a>
          </div>
        </div>
      )}
    </>
  );
};

// ==================== FOOTER ====================
const Footer = () => {
  return (
    <footer className="footer" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <h3 className="font-heading text-2xl text-white mb-4">Vivah Tents</h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Creating unforgettable events with premium tent and event rentals across the Greater Toronto Area.
            </p>
            <div className="flex gap-4">
              <a href="https://www.instagram.com/vivahtents/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#C5A059] transition-colors">
                <Instagram size={20} />
              </a>
              <a href="https://www.facebook.com/p/Vivah-Tents-GTA-100089740652389/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#C5A059] transition-colors">
                <Facebook size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white uppercase tracking-widest text-sm mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li><Link to="/" className="footer-link text-sm">Home</Link></li>
              <li><Link to="/about" className="footer-link text-sm">About Us</Link></li>
              <li><Link to="/services" className="footer-link text-sm">Services</Link></li>
              <li><Link to="/gallery" className="footer-link text-sm">Gallery</Link></li>
              <li><Link to="/reviews" className="footer-link text-sm">Reviews</Link></li>
              <li><Link to="/quote" className="footer-link text-sm">Get Quote</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white uppercase tracking-widest text-sm mb-6">Services</h4>
            <ul className="space-y-3">
              <li><span className="footer-link text-sm">Tent Rentals</span></li>
              <li><span className="footer-link text-sm">Table & Chair Rentals</span></li>
              <li><span className="footer-link text-sm">Floor Wraps</span></li>
              <li><span className="footer-link text-sm">Event Decor</span></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white uppercase tracking-widest text-sm mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Phone size={16} className="text-[#C5A059]" />
                <a href="tel:6473939872" className="hover:text-[#C5A059] transition-colors">(647) 393-9872</a>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Mail size={16} className="text-[#C5A059]" />
                <a href="mailto:vivahtents@gmail.com" className="hover:text-[#C5A059] transition-colors">vivahtents@gmail.com</a>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <Clock size={16} className="text-[#C5A059] mt-0.5" />
                <span>Mon - Sun: 9:00 AM - 7:00 PM</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <MapPin size={16} className="text-[#C5A059] mt-0.5" />
                <span>Serving GTA, Caledon, Guelph, Hamilton</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-gray-800 pt-8">
          <p className="text-center text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Vivah Tents. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// ==================== HOME PAGE ====================
const Home = () => {
  const [reviews, setReviews] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsRes, servicesRes] = await Promise.all([
          axios.get(`${API}/reviews`),
          axios.get(`${API}/services`)
        ]);
        setReviews(reviewsRes.data);
        setServices(servicesRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="w-full bg-white pt-24" data-testid="hero-section">
        <div className="w-full">
          <div className="relative min-h-[70vh] overflow-hidden">
            <div 
              className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
              style={{ 
                backgroundImage: `url('https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/onf62ki7_homepage.jpg')`
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/50" />
            <div className="relative z-10 flex flex-col items-center justify-center min-h-[70vh] text-center text-white px-4">
              <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-4">Premium Event Rentals</p>
              <h1 className="font-heading text-4xl md:text-6xl font-medium mb-6 leading-tight">
                Creating Unforgettable <br className="hidden md:block" />
                <span className="text-[#C5A059]">Celebrations</span>
              </h1>
              <p className="text-base md:text-lg text-gray-200 mb-8 max-w-xl mx-auto font-light">
                Luxury tent and event rentals for weddings, corporate events, and special occasions across the Greater Toronto Area.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/quote" data-testid="hero-cta-quote">
                  <Button className="bg-[#C5A059] hover:bg-[#B08D45] text-white rounded-none px-8 py-5 uppercase tracking-widest text-sm w-full sm:w-auto">
                    Request a Quote
                  </Button>
                </Link>
                <Link to="/services" data-testid="hero-cta-services">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] rounded-none px-8 py-5 uppercase tracking-widest text-sm w-full sm:w-auto">
                    View Services
                  </Button>
                </Link>
              </div>
              <div className="mt-8 bg-white/80 backdrop-blur-sm rounded-xl p-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/b1ln6q90_Logo%20vivah%20tents03-01-11-34-07.jpg" 
                  alt="Vivah Tents Logo" 
                  className="h-28 md:h-32 w-auto"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Rating Badge */}
        <div className="flex justify-center py-8 bg-white">
          <div className="bg-white shadow-lg px-6 py-4 flex items-center gap-4 border border-gray-100" data-testid="rating-badge">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} className="fill-[#C5A059] text-[#C5A059]" />
              ))}
            </div>
            <div className="h-8 w-px bg-gray-200" />
            <div>
              <p className="text-sm font-medium text-[#1A1A1A]">4.4 Rating</p>
              <p className="text-xs text-gray-500">31 Reviews on Google</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 md:py-32 px-6 md:px-12" data-testid="services-preview">
        <div className="max-w-7xl mx-auto">
          <div className="section-heading">
            <p className="caption">What We Offer</p>
            <h2>Our Services</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const serviceLinks = {
                "1": "/services/luxury-tents",
                "2": "/services/tables-chairs",
                "3": "/services/floor-wraps",
                "4": "/services/event-decor"
              };
              const linkTo = serviceLinks[service.id] || "/services";
              
              return (
                <Link 
                  to={linkTo}
                  key={service.id} 
                  className="service-card animate-fade-in-up cursor-pointer"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="service-card-image"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-xl mb-2 group-hover:text-[#C5A059]">{service.title}</h3>
                    <p className="text-gray-500 text-sm mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.slice(0, 2).map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <Check size={14} className="text-[#C5A059]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services" data-testid="view-all-services">
              <Button variant="outline" className="border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white rounded-none px-8 py-4 uppercase tracking-widest text-sm">
                View All Services <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Preview */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#FDFCF8]" data-testid="reviews-preview">
        <div className="max-w-7xl mx-auto">
          <div className="section-heading">
            <p className="caption">Testimonials</p>
            <h2>What Our Clients Say</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {reviews.slice(0, 3).map((review, index) => (
              <div 
                key={review.id} 
                className="review-card animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`review-card-${review.id}`}
              >
                <div className="flex mb-4 mt-8">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} size={16} className="fill-[#C5A059] text-[#C5A059]" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">{review.text}</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[#1A1A1A]">{review.author}</p>
                    <p className="text-sm text-gray-400">{review.date}</p>
                  </div>
                  <span className="text-xs uppercase tracking-wider text-gray-400">{review.source}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/reviews" data-testid="view-all-reviews">
              <Button variant="outline" className="border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white rounded-none px-8 py-4 uppercase tracking-widest text-sm">
                Read All Reviews <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#1A1A1A]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-4">Ready to Plan Your Event?</p>
          <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
            Let's Create Something Beautiful Together
          </h2>
          <p className="text-gray-400 mb-10 max-w-2xl mx-auto">
            Contact us today for a free consultation and quote. We serve the entire GTA, Caledon, Guelph, and Hamilton areas.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link to="/quote" data-testid="cta-quote-btn">
              <Button className="bg-[#C5A059] hover:bg-[#B08D45] text-white rounded-none px-10 py-6 uppercase tracking-widest text-sm">
                Get Your Free Quote
              </Button>
            </Link>
            <a href="tel:6473939872" className="flex items-center gap-3 text-white hover:text-[#C5A059] transition-colors" data-testid="cta-phone">
              <Phone size={20} />
              <span className="text-lg">(647) 393-9872</span>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Bar */}
      <section className="py-8 px-6 md:px-12 bg-[#C5A059]" data-testid="contact-bar">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-8 flex-wrap justify-center">
            <a href="tel:6473939872" className="flex items-center gap-2 text-white hover:text-[#1A1A1A] transition-colors">
              <Phone size={18} />
              <span>(647) 393-9872</span>
            </a>
            <a href="mailto:vivahtents@gmail.com" className="flex items-center gap-2 text-white hover:text-[#1A1A1A] transition-colors">
              <Mail size={18} />
              <span>vivahtents@gmail.com</span>
            </a>
            <div className="flex items-center gap-2 text-white">
              <Clock size={18} />
              <span>9 AM - 7 PM</span>
            </div>
          </div>
          <Link to="/quote" className="text-white hover:text-[#1A1A1A] transition-colors flex items-center gap-2" data-testid="contact-bar-quote">
            <span className="uppercase tracking-widest text-sm">Request Quote</span>
            <ChevronRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// ==================== LUXURY TENTS PAGE ====================
const LuxuryTentsPage = () => {
  const products = [
    {
      id: 1,
      name: "40x100 Clear Top Tent",
      description: "Stunning clear top tent perfect for stargazing and evening events. Features string lights and elegant window panels. Accommodates 200+ guests.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/7prj9gtd_ea1d2027-9509-4c64-8891-9c5c892ab3bc.JPG"
    },
    {
      id: 2,
      name: "Traditional Ceremony Tent",
      description: "Elegant white draped tent perfect for Sikh ceremonies, Anand Karaj, and traditional wedding functions. Features beautiful floor cushion seating.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/yuhpokvo_ddfd97d4-4064-4b98-a557-ecc61fee204c.JPG"
    },
    {
      id: 3,
      name: "Mehndi & Sangeet Tent",
      description: "Vibrant colorful tent setup perfect for mehndi nights and sangeet celebrations. Features clear top with pink, yellow, and orange draping.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/tad2qrjd_818c537f-2f3c-4e25-aac7-f0d6b9e887ed.JPG"
    },
    {
      id: 4,
      name: "Indian Canopy Tents",
      description: "Beautiful traditional Indian canopy tents (shamianas). Available in red velvet and white embroidered designs. Perfect for outdoor ceremonies.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/jram01iv_7ef7f037-f861-4bb1-97b5-2c5f0c7ced85.JPG"
    },
    {
      id: 5,
      name: "Elegant Draped Tent",
      description: "Luxurious beige draped tent with fairy lights and crystal chandeliers. Perfect for upscale weddings and receptions. Creates a magical ambiance.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/vzmb3d6a_ace2c5ac-4eff-48b6-898e-89fedf5a1290.JPG"
    },
    {
      id: 6,
      name: "Backyard Clear Top Tent",
      description: "Intimate backyard tent with clear top, string lights, and elegant draping. Perfect for small gatherings and intimate celebrations.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/k4fg3b48_8c04e256-503a-4567-9df0-cd167afb9edc.JPG"
    },
    {
      id: 7,
      name: "Driveway Tent",
      description: "Accommodate extra space with our 20x20 driveway tent. Perfect for expanding your event area, cocktail hours, or additional guest seating.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/aorgw6rp_bdc70094-87e3-40cf-bfb3-2af09311be42%202.JPG"
    },
    {
      id: 8,
      name: "Patterned Ceiling Tent",
      description: "Large tent featuring decorative patterned ceiling liner, dance floor, and crystal chandeliers. Perfect for grand celebrations.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/cw9pcz5l_8461C9B9-5359-42A5-9399-B2ED7703D010.JPG"
    },
    {
      id: 9,
      name: "Moroccan Style Tent",
      description: "Elegant white draped tent with stunning blue and white Moroccan floor wrap. Features chandeliers and gold chaivari chairs.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/beoighoc_02dd51e1-9f7e-498d-8d9b-96a1b6b3f8a1.JPG"
    },
    {
      id: 10,
      name: "Large Event Tent",
      description: "Spacious white frame tent with window panels. Ideal for community events, festivals, religious gatherings, and large celebrations.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/avf7v7g8_b943a960-0699-482e-beb2-b1bcacbcc06e.JPG"
    }
  ];

  return (
    <div className="pt-20" data-testid="luxury-tents-page">
      {/* Header */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto text-center">
          <Link to="/services" className="inline-flex items-center gap-2 text-[#C5A059] mb-6 hover:underline">
            <ChevronRight size={16} className="rotate-180" />
            Back to Services
          </Link>
          <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-4">Premium Selection</p>
          <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">Luxury Tent Rentals</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            From clear top tents to traditional Indian canopies, we offer premium tent solutions for every occasion.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <div 
                key={product.id}
                className="group animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`tent-product-${product.id}`}
              >
                <div className="overflow-hidden mb-4 aspect-square">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-heading text-xl mb-2">{product.name}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-[#C5A059]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">Ready to Book Your Tent?</h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Contact us for pricing and availability. We deliver and set up across the GTA, Caledon, Guelph, and Hamilton.
          </p>
          <Link to="/quote" data-testid="luxury-tents-cta">
            <Button className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-none px-10 py-6 uppercase tracking-widest text-sm">
              Get a Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

// ==================== FLOOR WRAPS PAGE ====================
const FloorWrapsPage = () => {
  const products = [
    {
      id: 1,
      name: "Mandala Carpet",
      description: "Stunning black and gold mandala pattern carpet. Perfect for cultural events, mehndi nights, and elegant celebrations.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/ygc4wqd0_7b5817e2-1dc6-49b5-a983-ed9e7e70c101.JPG"
    },
    {
      id: 2,
      name: "Designer Carpet",
      description: "Elegant black and gold paisley pattern carpet. Creates a luxurious foundation for your event space.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/rma0ksct_1639c912-d53a-44c3-874d-28ca11741f0e.JPG"
    },
    {
      id: 3,
      name: "Green Turf Carpet",
      description: "Add green turf carpet to your tent booking. Perfect for outdoor-themed events and garden parties.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/d9vwzl1f_87c68ee8-8357-4913-8269-78476b7db62a.JPG"
    },
    {
      id: 4,
      name: "Stage Platforms",
      description: "Professional portable stage platforms. Perfect for performances, speeches, and ceremony focal points.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/z8fgqcg8_c2e9f809-e9d0-407d-aadb-df07f49c0d99.JPG"
    },
    {
      id: 5,
      name: "Damask Aisle Runner",
      description: "Stunning red and white damask pattern floor wrap. Perfect for wedding aisles and grand entrances.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/70ds8oci_IMG_5633.jpg"
    },
    {
      id: 6,
      name: "Duratrac Dance Floor",
      description: "Professional duratrac flooring for dance floors and stabilizing uneven ground. Creates a solid, level surface for any outdoor event.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/99m9r3z2_duratrac%20floor.JPG"
    }
  ];

  return (
    <div className="pt-20" data-testid="floor-wraps-page">
      {/* Header */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto text-center">
          <Link to="/services" className="inline-flex items-center gap-2 text-[#C5A059] mb-6 hover:underline">
            <ChevronRight size={16} className="rotate-180" />
            Back to Services
          </Link>
          <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-4">Transform Any Space</p>
          <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">Floor Wraps & Carpeting</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Premium floor coverings, custom wraps, and stage platforms to transform any venue.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <div 
                key={product.id}
                className="group animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`floor-product-${product.id}`}
              >
                <div className="overflow-hidden mb-4 aspect-square">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-heading text-xl mb-2">{product.name}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>
              </div>
            ))}
          </div>

          {/* Instagram CTA */}
          <div className="mt-20 text-center animate-fade-in-up">
            <a 
              href="https://www.instagram.com/vivahtentswraps/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex flex-col items-center group"
              data-testid="floor-wraps-instagram"
            >
              <Instagram size={56} className="text-[#C5A059] mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="font-heading text-2xl md:text-3xl text-[#1A1A1A] mb-2">
                See More Floor Wrap Designs
              </h3>
              <p className="text-[#C5A059] text-xl font-semibold uppercase tracking-widest group-hover:underline">
                Follow @vivahtentswraps on Instagram
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-[#C5A059]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">Need Floor Wraps for Your Event?</h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Contact us for custom designs and pricing. We deliver and install across the GTA.
          </p>
          <Link to="/quote" data-testid="floor-wraps-cta">
            <Button className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-none px-10 py-6 uppercase tracking-widest text-sm">
              Get a Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

// ==================== EVENT DECOR PAGE ====================
const EventDecorPage = () => {
  const products = [
    {
      id: 1,
      name: "Loveseat",
      description: "Elegant white loveseat with gold legs. Perfect for photo areas, lounges, and VIP seating arrangements.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/cv1ku3f9_96d02d0d-1616-4290-9cdd-2bafa69d812c.JPG"
    },
    {
      id: 2,
      name: "Indian Maanjas",
      description: "Traditional Indian charpai/maanja beds with colorful woven patterns. Perfect for mehndi nights and cultural celebrations.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/021kowi3_a976f41a-3486-41c8-a330-d83add86b65c.JPG"
    },
    {
      id: 3,
      name: "Crystal Chandeliers",
      description: "Stunning crystal chandeliers that add glamour and elegance to any tent setup. Creates a luxurious ambiance.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/j5m4gdtr_cd1f9585-f440-4e1b-a520-3fe691161155.JPG"
    },
    {
      id: 4,
      name: "Black and Gold Bar",
      description: "Elegant black and gold geometric bar. Perfect for cocktail hours, receptions, and upscale events.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/nu6i45s4_2900c2a1-59b0-41cf-9576-a35fa5e1687c.JPG"
    },
    {
      id: 5,
      name: "Patio Heaters",
      description: "Powerful outdoor patio heaters to keep guests comfortable during cooler evenings and fall events.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/rd2dqdxs_b143198b-7c6c-4201-8149-b9cf91dad78e.JPG"
    },
    {
      id: 6,
      name: "Industrial Fans",
      description: "High-powered industrial fans for air circulation during summer events. Keep your guests cool and comfortable.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/bz0nqskt_be9f599a-9f0c-44b2-8e72-acc1cdf447f2.JPG"
    }
  ];

  return (
    <div className="pt-20" data-testid="event-decor-page">
      {/* Header */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto text-center">
          <Link to="/services" className="inline-flex items-center gap-2 text-[#C5A059] mb-6 hover:underline">
            <ChevronRight size={16} className="rotate-180" />
            Back to Services
          </Link>
          <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-4">Our Collection</p>
          <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">Event Decor & Accessories</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Complete your event with our curated collection of decor items, lighting, and comfort accessories.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <div 
                key={product.id}
                className="group animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`decor-product-${product.id}`}
              >
                <div className="overflow-hidden mb-4 aspect-square">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-heading text-xl mb-2">{product.name}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-[#C5A059]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">Need Decor for Your Event?</h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Contact us for pricing and availability. We deliver and set up across the GTA.
          </p>
          <Link to="/quote" data-testid="event-decor-cta">
            <Button className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-none px-10 py-6 uppercase tracking-widest text-sm">
              Get a Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

// ==================== TABLES & CHAIRS PAGE ====================
const TablesChairsPage = () => {
  const products = [
    {
      id: 1,
      name: "6ft Long Tables",
      description: "Perfect for banquet-style seating, buffet setups, and head tables. Seats 6-8 guests comfortably.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/0jd9m2hs_2b163eb5-f3c6-4606-9f3b-3822de1b3973.JPG"
    },
    {
      id: 2,
      name: "5ft Round Tables",
      description: "Ideal for elegant dining setups. Seats 8-10 guests and creates beautiful circular seating arrangements.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/ftpodgpc_55b1e519-870c-4a34-9482-1233fb50cd9a.JPG"
    },
    {
      id: 3,
      name: "Cocktail Tables",
      description: "Perfect for cocktail hours and standing receptions. Compact and elegant for mingling spaces.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/zsocn2j0_1792249d-9f84-4ec3-8c39-906bf3285f41.JPG"
    },
    {
      id: 4,
      name: "Modern Bar Tables",
      description: "Stylish modern bar tables perfect for poolside events, lounges, and contemporary celebrations.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/cp8owrd0_db109ccf-adfd-45ea-8a2e-cbe46f611181.JPG"
    },
    {
      id: 5,
      name: "White Resin Chairs",
      description: "Elegant and durable white resin chairs perfect for weddings and formal events. Easy to clean and weather-resistant.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/tl9po78e_91a054aa-3056-4362-98a3-d6d154c1d300.JPG"
    },
    {
      id: 6,
      name: "Folding Chairs",
      description: "Practical and versatile folding chairs suitable for any event. Comfortable seating with easy setup and storage.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/0us26urq_97dc9a45-c023-47e5-ae22-2a0a59750b53.JPG"
    },
    {
      id: 7,
      name: "Clear Chaivari Chairs",
      description: "Stunning transparent chaivari chairs that add a touch of modern elegance to any event. Perfect for upscale weddings and galas.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/g8zp16t9_98af879e-a7f4-43d2-b41f-ba33a7ee94d9.JPG"
    },
    {
      id: 8,
      name: "Gold Chaivari Chairs",
      description: "Classic gold chaivari chairs that bring timeless elegance and luxury to any event. A perfect choice for upscale celebrations.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/odsc0xow_d58b4c25-df73-4663-a4f8-6caf3bfa8b9f.JPG"
    },
    {
      id: 9,
      name: "Princess Acrylic Chairs",
      description: "Elegant transparent acrylic chairs with ornate Victorian-style design. Perfect for luxury weddings, princess-themed events, and upscale celebrations.",
      image: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/buze9ewn_princess%20arcylic%20chairs.PNG"
    }
  ];

  return (
    <div className="pt-20" data-testid="tables-chairs-page">
      {/* Header */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto text-center">
          <Link to="/services" className="inline-flex items-center gap-2 text-[#C5A059] mb-6 hover:underline">
            <ChevronRight size={16} className="rotate-180" />
            Back to Services
          </Link>
          <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-4">Our Inventory</p>
          <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">Tables & Chairs</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Elegant seating and table solutions for events of all sizes. From intimate gatherings to grand celebrations.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <div 
                key={product.id}
                className="group animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`product-${product.id}`}
              >
                <div className="overflow-hidden mb-4 aspect-square">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-heading text-xl mb-2">{product.name}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-[#C5A059]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">Need Tables & Chairs for Your Event?</h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Contact us for pricing and availability. We deliver and set up across the GTA.
          </p>
          <Link to="/quote" data-testid="tables-chairs-cta">
            <Button className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-none px-10 py-6 uppercase tracking-widest text-sm">
              Get a Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

// ==================== SERVICES PAGE ====================
const ServicesPage = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services`);
        setServices(res.data);
      } catch (e) {
        console.error("Error fetching services:", e);
      }
    };
    fetchServices();
  }, []);

  return (
    <div className="pt-20" data-testid="services-page">
      {/* Header */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-4">Our Offerings</p>
          <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">Premium Event Services</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            From elegant tents to sophisticated decor, we provide everything you need to create a memorable event.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {services.map((service, index) => {
              const isLuxuryTents = service.id === "1";
              const isTablesChairs = service.id === "2";
              const isFloorWraps = service.id === "3";
              const isEventDecor = service.id === "4";
              const hasSubpage = isLuxuryTents || isTablesChairs || isFloorWraps || isEventDecor;
              const ServiceWrapper = hasSubpage ? Link : 'div';
              const linkTo = isLuxuryTents ? "/services/luxury-tents" : isTablesChairs ? "/services/tables-chairs" : isFloorWraps ? "/services/floor-wraps" : isEventDecor ? "/services/event-decor" : "";
              const wrapperProps = hasSubpage ? { to: linkTo } : {};
              
              return (
                <ServiceWrapper
                  key={service.id} 
                  className={`group animate-fade-in-up ${hasSubpage ? 'cursor-pointer' : ''}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`service-detail-${service.id}`}
                  {...wrapperProps}
                >
                  <div className="overflow-hidden mb-6 relative">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    {hasSubpage && (
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 text-white uppercase tracking-widest text-sm font-medium transition-opacity">
                          View Collection →
                        </span>
                      </div>
                    )}
                  </div>
                  <h2 className="font-heading text-3xl mb-4 group-hover:text-[#C5A059] transition-colors">{service.title}</h2>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <ul className="grid grid-cols-2 gap-3">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-600">
                        <Check size={16} className="text-[#C5A059]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {hasSubpage && (
                    <p className="mt-4 text-[#C5A059] font-medium uppercase tracking-wider text-sm group-hover:underline">
                      View Full Collection →
                    </p>
                  )}
                </ServiceWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#FDFCF8]" data-testid="service-areas">
        <div className="max-w-7xl mx-auto">
          <div className="section-heading">
            <p className="caption">Coverage</p>
            <h2>Areas We Serve</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {["Greater Toronto Area", "Brampton", "Mississauga", "Caledon", "Guelph", "Hamilton", "Scarborough", "Markham", "Oakville", "Milton"].map((area, i) => (
              <div key={i} className="bg-white border border-gray-100 p-4 text-center">
                <p className="text-gray-700">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-[#C5A059]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">Ready to Get Started?</h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Tell us about your event and we'll provide a customized quote within 24 hours.
          </p>
          <Link to="/quote" data-testid="services-cta-quote">
            <Button className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-none px-10 py-6 uppercase tracking-widest text-sm">
              Request a Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

// ==================== REVIEWS PAGE ====================
const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${API}/reviews`);
        setReviews(res.data);
      } catch (e) {
        console.error("Error fetching reviews:", e);
      }
    };
    fetchReviews();
  }, []);

  return (
    <div className="pt-20" data-testid="reviews-page">
      {/* Header */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-4">Testimonials</p>
          <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">Client Reviews</h1>
          <div className="flex items-center justify-center gap-4 mt-8">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={24} className="fill-[#C5A059] text-[#C5A059]" />
              ))}
            </div>
            <span className="text-white text-xl">4.4 / 5</span>
            <span className="text-gray-400">Based on 31 reviews</span>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {reviews.map((review, index) => (
              <div 
                key={review.id} 
                className="review-card animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`review-detail-${review.id}`}
              >
                <div className="flex mb-4 mt-8">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} size={18} className="fill-[#C5A059] text-[#C5A059]" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed text-lg">{review.text}</p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div>
                    <p className="font-medium text-[#1A1A1A] text-lg">{review.author}</p>
                    <p className="text-sm text-gray-400">{review.date}</p>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <span className="text-xs uppercase tracking-wider">via {review.source}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-[#FDFCF8]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl mb-6">Ready to Experience Our Service?</h2>
          <p className="text-gray-600 mb-8 max-w-xl mx-auto">
            Join our satisfied clients and let us make your next event truly special.
          </p>
          <Link to="/quote" data-testid="reviews-cta-quote">
            <Button className="bg-[#C5A059] hover:bg-[#B08D45] text-white rounded-none px-10 py-6 uppercase tracking-widest text-sm">
              Get Your Free Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

// ==================== QUOTE PAGE ====================
const QuotePage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    event_date: "",
    event_type: "",
    guest_count: "",
    venue_location: "",
    items_needed: [],
    budget: "",
    additional_notes: ""
  });
  const [date, setDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const eventTypes = [
    "Wedding",
    "Engagement Party",
    "Corporate Event",
    "Birthday Party",
    "Religious Ceremony",
    "Anniversary",
    "Other"
  ];

  const guestCounts = [
    "1-50 guests",
    "51-100 guests",
    "101-200 guests",
    "201-300 guests",
    "300+ guests"
  ];

  const budgetRanges = [
    "Under $1,000",
    "$1,000 - $2,500",
    "$2,500 - $5,000",
    "$5,000 - $10,000",
    "$10,000+"
  ];

  const rentalItems = [
    { id: "tents", label: "Tents" },
    { id: "chairs", label: "Chairs" },
    { id: "tables", label: "Tables" },
    { id: "floor_wraps", label: "Floor Wraps" },
    { id: "lighting", label: "Lighting" },
    { id: "draping", label: "Draping" },
    { id: "dance_floor", label: "Dance Floor" },
    { id: "stage", label: "Stage/Platform" }
  ];

  const handleItemToggle = (itemId) => {
    setFormData(prev => ({
      ...prev,
      items_needed: prev.items_needed.includes(itemId)
        ? prev.items_needed.filter(id => id !== itemId)
        : [...prev.items_needed, itemId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        event_date: date ? format(date, "yyyy-MM-dd") : ""
      };

      await axios.post(`${API}/quotes`, payload);
      setSubmitted(true);
      toast.success("Quote request submitted successfully! We'll contact you within 24 hours.");
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Failed to submit quote. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center px-6" data-testid="quote-success">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-[#C5A059]/10 flex items-center justify-center mx-auto mb-6">
            <Check size={32} className="text-[#C5A059]" />
          </div>
          <h1 className="font-heading text-3xl mb-4">Thank You!</h1>
          <p className="text-gray-600 mb-8">
            Your quote request has been received. We'll review your requirements and get back to you within 24 hours.
          </p>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">Need immediate assistance?</p>
            <a href="tel:6473939872" className="flex items-center justify-center gap-2 text-[#C5A059] font-medium">
              <Phone size={18} />
              (647) 393-9872
            </a>
          </div>
          <Link to="/" className="mt-8 inline-block">
            <Button variant="outline" className="border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white rounded-none px-8 py-4 uppercase tracking-widest text-sm">
              Return Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="quote-page">
      {/* Header */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-4">Get Started</p>
          <h1 className="font-heading text-4xl md:text-5xl text-white mb-6">Request a Quote</h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Tell us about your event and we'll provide a customized quote within 24 hours. All inquiries are free with no obligation.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="quote-form-container" data-testid="quote-form">
            {/* Contact Information */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl mb-6 pb-4 border-b border-gray-100">Contact Information</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="form-label">Full Name *</Label>
                  <Input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="rounded-none border-gray-200 focus:border-[#C5A059] focus:ring-0"
                    placeholder="John Smith"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <Label className="form-label">Email Address *</Label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="rounded-none border-gray-200 focus:border-[#C5A059] focus:ring-0"
                    placeholder="john@example.com"
                    data-testid="input-email"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="form-label">Phone Number *</Label>
                  <Input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="rounded-none border-gray-200 focus:border-[#C5A059] focus:ring-0"
                    placeholder="(647) 123-4567"
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl mb-6 pb-4 border-b border-gray-100">Event Details</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="form-label">Event Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal rounded-none border-gray-200 hover:border-[#C5A059]"
                        data-testid="input-date"
                      >
                        {date ? format(date, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        initialFocus
                        disabled={(date) => date < new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="form-label">Event Type *</Label>
                  <Select
                    value={formData.event_type}
                    onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                  >
                    <SelectTrigger className="rounded-none border-gray-200 focus:border-[#C5A059]" data-testid="input-event-type">
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">Guest Count *</Label>
                  <Select
                    value={formData.guest_count}
                    onValueChange={(value) => setFormData({ ...formData, guest_count: value })}
                  >
                    <SelectTrigger className="rounded-none border-gray-200 focus:border-[#C5A059]" data-testid="input-guest-count">
                      <SelectValue placeholder="Select guest count" />
                    </SelectTrigger>
                    <SelectContent>
                      {guestCounts.map((count) => (
                        <SelectItem key={count} value={count}>{count}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">Venue Location *</Label>
                  <Input
                    type="text"
                    required
                    value={formData.venue_location}
                    onChange={(e) => setFormData({ ...formData, venue_location: e.target.value })}
                    className="rounded-none border-gray-200 focus:border-[#C5A059] focus:ring-0"
                    placeholder="City or full address"
                    data-testid="input-venue"
                  />
                </div>
              </div>
            </div>

            {/* Items Needed */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl mb-6 pb-4 border-b border-gray-100">Items Needed</h2>
              <p className="text-gray-500 text-sm mb-6">Select all items you're interested in renting</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="items-checklist">
                {rentalItems.map((item) => (
                  <label
                    key={item.id}
                    htmlFor={`checkbox-${item.id}`}
                    className={`checkbox-item ${formData.items_needed.includes(item.id) ? "selected" : ""}`}
                    data-testid={`item-${item.id}`}
                  >
                    <Checkbox
                      id={`checkbox-${item.id}`}
                      checked={formData.items_needed.includes(item.id)}
                      onCheckedChange={() => handleItemToggle(item.id)}
                      className="border-gray-300 data-[state=checked]:bg-[#C5A059] data-[state=checked]:border-[#C5A059]"
                    />
                    <span className="text-sm">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Budget & Notes */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl mb-6 pb-4 border-b border-gray-100">Additional Information</h2>
              <div className="space-y-6">
                <div>
                  <Label className="form-label">Budget Range (Optional)</Label>
                  <Select
                    value={formData.budget}
                    onValueChange={(value) => setFormData({ ...formData, budget: value })}
                  >
                    <SelectTrigger className="rounded-none border-gray-200 focus:border-[#C5A059]" data-testid="input-budget">
                      <SelectValue placeholder="Select budget range" />
                    </SelectTrigger>
                    <SelectContent>
                      {budgetRanges.map((range) => (
                        <SelectItem key={range} value={range}>{range}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">Additional Notes (Optional)</Label>
                  <Textarea
                    value={formData.additional_notes}
                    onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                    className="rounded-none border-gray-200 focus:border-[#C5A059] focus:ring-0 min-h-[120px]"
                    placeholder="Tell us more about your event or any specific requirements..."
                    data-testid="input-notes"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#C5A059] hover:bg-[#B08D45] text-white rounded-none py-6 uppercase tracking-widest text-sm"
              data-testid="submit-quote-btn"
            >
              {isSubmitting ? "Submitting..." : "Submit Quote Request"}
            </Button>

            <p className="text-center text-sm text-gray-500 mt-6">
              By submitting this form, you agree to be contacted regarding your inquiry.
            </p>
          </form>

          {/* Contact Alternative */}
          <div className="mt-12 text-center">
            <p className="text-gray-500 mb-4">Prefer to speak with someone directly?</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a href="tel:6473939872" className="flex items-center gap-2 text-[#C5A059] font-medium hover:text-[#B08D45] transition-colors">
                <Phone size={18} />
                (647) 393-9872
              </a>
              <a href="mailto:vivahtents@gmail.com" className="flex items-center gap-2 text-[#C5A059] font-medium hover:text-[#B08D45] transition-colors">
                <Mail size={18} />
                vivahtents@gmail.com
              </a>
              <a href="https://www.instagram.com/vivahtents/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#C5A059] font-medium hover:text-[#B08D45] transition-colors" data-testid="quote-instagram-link">
                <Instagram size={18} />
                @vivahtents
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ==================== ABOUT PAGE ====================
const AboutPage = () => {
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Header */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-4">Our Story</p>
          <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">About Vivah Tents</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Creating your event with luxury since 2014
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in-up">
              <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-4">Est. 2014</p>
              <h2 className="font-heading text-3xl md:text-4xl mb-6">A Decade of Excellence in Event Rentals</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Vivah Tents has been a trusted name in tent and event rentals for over 10 years. What started as a small family business in 2014 has grown into one of the Greater Toronto Area's premier event rental companies, serving thousands of satisfied customers across weddings, corporate events, religious ceremonies, and private celebrations.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our name "Vivah" comes from the Sanskrit word for wedding or marriage ceremony, reflecting our deep roots in creating beautiful celebrations for South Asian weddings and events. Today, we proudly serve clients from all backgrounds, bringing the same level of elegance and attention to detail to every event we touch.
              </p>
              <p className="text-gray-600 leading-relaxed">
                We believe every event deserves to be extraordinary. From intimate backyard gatherings to grand celebrations with hundreds of guests, our team works tirelessly to transform your vision into reality with our premium tents, elegant furniture, stunning decor, and exceptional service.
              </p>
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '200ms' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/pad0iuxg_IMG_2352.jpeg"
                alt="Vivah Tents elegant setup"
                className="w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6 md:px-12 bg-[#FDFCF8]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center animate-fade-in-up">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#C5A059]/10 rounded-full flex items-center justify-center">
                <CalendarIcon size={28} className="text-[#C5A059]" />
              </div>
              <p className="font-heading text-4xl text-[#1A1A1A] mb-2">10+</p>
              <p className="text-gray-500 text-sm uppercase tracking-wider">Years Experience</p>
            </div>
            <div className="text-center animate-fade-in-up" style={{ animationDelay: '100ms' }}>
              <div className="w-16 h-16 mx-auto mb-4 bg-[#C5A059]/10 rounded-full flex items-center justify-center">
                <Heart size={28} className="text-[#C5A059]" />
              </div>
              <p className="font-heading text-4xl text-[#1A1A1A] mb-2">1000+</p>
              <p className="text-gray-500 text-sm uppercase tracking-wider">Events Served</p>
            </div>
            <div className="text-center animate-fade-in-up" style={{ animationDelay: '200ms' }}>
              <div className="w-16 h-16 mx-auto mb-4 bg-[#C5A059]/10 rounded-full flex items-center justify-center">
                <Users size={28} className="text-[#C5A059]" />
              </div>
              <p className="font-heading text-4xl text-[#1A1A1A] mb-2">500+</p>
              <p className="text-gray-500 text-sm uppercase tracking-wider">Happy Clients</p>
            </div>
            <div className="text-center animate-fade-in-up" style={{ animationDelay: '300ms' }}>
              <div className="w-16 h-16 mx-auto mb-4 bg-[#C5A059]/10 rounded-full flex items-center justify-center">
                <Award size={28} className="text-[#C5A059]" />
              </div>
              <p className="font-heading text-4xl text-[#1A1A1A] mb-2">4.4</p>
              <p className="text-gray-500 text-sm uppercase tracking-wider">Google Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="section-heading">
            <p className="caption">What We Offer</p>
            <h2>Premium Event Solutions</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 border border-gray-100 animate-fade-in-up">
              <h3 className="font-heading text-2xl mb-4">Tent Rentals</h3>
              <p className="text-gray-600 leading-relaxed">
                We offer a wide variety of tents to accommodate events of all sizes, from intimate gatherings of 10 guests to grand celebrations with 500+ attendees. Our collection includes frame tents, clear top tents, and custom configurations to suit your venue and vision.
              </p>
            </div>
            <div className="p-8 border border-gray-100 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
              <h3 className="font-heading text-2xl mb-4">Tables & Chairs</h3>
              <p className="text-gray-600 leading-relaxed">
                From elegant chaivari chairs to classic banquet seating, we provide premium furniture that elevates your event. Our inventory includes round tables, rectangular tables, cocktail tables, and lounge furniture in various styles and finishes.
              </p>
            </div>
            <div className="p-8 border border-gray-100 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
              <h3 className="font-heading text-2xl mb-4">Floor Wraps & Dance Floors</h3>
              <p className="text-gray-600 leading-relaxed">
                Transform any surface with our premium floor coverings. We offer vinyl wraps, red carpets, elegant dance floors, and stage platforms that create the perfect foundation for your celebration.
              </p>
            </div>
            <div className="p-8 border border-gray-100 animate-fade-in-up" style={{ animationDelay: '300ms' }}>
              <h3 className="font-heading text-2xl mb-4">Decor & Accessories</h3>
              <p className="text-gray-600 leading-relaxed">
                Complete your vision with our curated collection of decor items including lighting, draping, centerpieces, backdrops, and more. Our team can help you create a cohesive look that matches your event theme.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 px-6 md:px-12 bg-[#FDFCF8]">
        <div className="max-w-6xl mx-auto">
          <div className="section-heading">
            <p className="caption">Service Areas</p>
            <h2>Proudly Serving the GTA & Beyond</h2>
          </div>

          <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
            We deliver and set up our premium event rentals across the Greater Toronto Area and surrounding regions. Our experienced team ensures timely delivery, professional setup, and seamless takedown for every event.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {["Brampton", "Mississauga", "Toronto", "Caledon", "Guelph", "Hamilton", "Scarborough", "Markham", "Oakville", "Milton"].map((area, i) => (
              <div key={i} className="bg-white border border-gray-100 p-4 text-center">
                <MapPin size={16} className="text-[#C5A059] mx-auto mb-2" />
                <p className="text-gray-700 text-sm">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-[#C5A059]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">Ready to Create Your Dream Event?</h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Let's work together to make your next celebration unforgettable. Contact us today for a free consultation and quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote" data-testid="about-cta-quote">
              <Button className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-none px-10 py-6 uppercase tracking-widest text-sm">
                Get a Free Quote
              </Button>
            </Link>
            <a href="tel:6473939872">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] rounded-none px-10 py-6 uppercase tracking-widest text-sm">
                Call Us Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// ==================== GALLERY PAGE ====================
const GalleryPage = () => {
  const galleryImages = [
    {
      id: 1,
      src: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/pad0iuxg_IMG_2352.jpeg",
      alt: "Elegant white tent setup with gold chaivari chairs",
      type: "image"
    },
    {
      id: 2,
      src: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/0d3srk03_IMG_2353.jpeg",
      alt: "Luxury tent interior with round tables",
      type: "image"
    },
    {
      id: 3,
      src: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/sobnyl61_6ed82f74-973d-4888-9525-078b01ba8cb7.jpeg",
      alt: "Clear top tent with elegant draping and lights",
      type: "image"
    },
    {
      id: 4,
      src: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/2cgo2611_7773db44-c534-4867-8dfd-06d9c659e193.jpeg",
      alt: "Rustic wooden tables with chaivari chairs",
      type: "image"
    },
    {
      id: 5,
      src: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/wkqutuz0_0F133FDC-FEC4-40FF-A1C3-02F1510FFF58.jpeg",
      alt: "Large outdoor tent setup with seating",
      type: "image"
    },
    {
      id: 6,
      src: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/0z0g10z3_67058529-7B81-420B-8667-336A363D960D.JPG",
      alt: "Outdoor event tents on sports field",
      type: "image"
    },
    {
      id: 7,
      src: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/653de8m5_3DA25101-147E-42E6-B703-73F91146BFDF.JPG",
      alt: "Clear top tent with colorful floral decor and pink pillars",
      type: "image"
    },
    {
      id: 8,
      src: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/edbuff2b_894370f5-62a2-4eb0-a16c-b37858b85d44.JPG",
      alt: "Traditional Indian tent with mandala floor wrap and colorful draping",
      type: "image"
    },
    {
      id: 9,
      src: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/ur61fohh_a3895c5e-b45a-4154-ae81-74e493b6e72a.JPG",
      alt: "Outdoor tent with pink cushions, lanterns and floral arrangements",
      type: "image"
    },
    {
      id: 10,
      src: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/i7c2v88l_b6564326-776b-442f-b19e-fff843705389.JPG",
      alt: "Night tent setup with fairy lights and custom dance floor",
      type: "image"
    },
    {
      id: 11,
      src: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/u4je5xzj_E262D796-D70B-4F02-9F19-AFE87BC99EA3.jpg",
      alt: "Large outdoor tent with colorful blue and purple draping",
      type: "image"
    },
    {
      id: 12,
      src: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/nu6i45s4_2900c2a1-59b0-41cf-9576-a35fa5e1687c.JPG",
      alt: "Elegant black and gold geometric bar",
      type: "image"
    },
    {
      id: 13,
      src: "https://customer-assets.emergentagent.com/job_tent-event-rentals/artifacts/slixakc6_132c7787-9b51-49c1-8e06-364c4f1fa8d0.mp4",
      alt: "Vivah Tents event setup video",
      type: "video"
    },
    {
      id: 14,
      src: "https://customer-assets.emergentagent.com/job_event-rentals-14/artifacts/arwwp0ad_decor.jpg",
      alt: "Elegant white tent with floral arch, gold chaivari chairs and chandelier",
      type: "image"
    },
    {
      id: 15,
      src: "https://customer-assets.emergentagent.com/job_event-rentals-14/artifacts/dz3241jo_IMG_5680.jpg",
      alt: "Outdoor event with red velvet canopy tent, pink draping and princess acrylic chairs",
      type: "image"
    },
    {
      id: 16,
      src: "https://customer-assets.emergentagent.com/job_event-rentals-14/artifacts/x44mxamv_IMG_5681.jpg",
      alt: "Poolside event setup with princess acrylic chairs, red canopy tent and round tables",
      type: "image"
    },
    {
      id: 17,
      src: "https://customer-assets.emergentagent.com/job_event-rentals-14/artifacts/zlv73uam_IMG_5684.jpg",
      alt: "Large clear top tent at night with string lights and princess chairs seating",
      type: "image"
    },
    {
      id: 18,
      src: "https://customer-assets.emergentagent.com/job_event-rentals-14/artifacts/4uf6bus5_Screenshot%202026-03-02%20at%202.15.38%E2%80%AFPM.png",
      alt: "Elegant beige draped tent interior with chandeliers, clear chaivari chairs and floral backdrop",
      type: "image"
    },
    {
      id: 19,
      src: "https://customer-assets.emergentagent.com/job_event-rentals-14/artifacts/e4jz33ek_IMG_5632.jpg",
      alt: "Colorful mandala floor wrap design under clear top tent with stage platform",
      type: "image"
    }
  ];

  return (
    <div className="pt-20" data-testid="gallery-page">
      {/* Header */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-4">Our Work</p>
          <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">Gallery</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Browse through our collection of stunning event setups. From intimate gatherings to grand celebrations, see how we transform spaces.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {galleryImages.map((image, index) => (
              <div 
                key={image.id}
                className={`relative overflow-hidden group cursor-pointer animate-fade-in-up ${
                  index === 0 || index === 3 ? 'md:col-span-2 lg:col-span-1' : ''
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`gallery-image-${image.id}`}
              >
                <div className="aspect-square overflow-hidden">
                  {image.type === "video" ? (
                    <video
                      src={image.src}
                      className="w-full h-full object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  ) : (
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading={index < 6 ? "lazy" : "eager"}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400">Image loading...</div>';
                      }}
                    />
                  )}
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-white text-center p-4">
                    <p className="text-sm uppercase tracking-widest">{image.alt}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* More Coming Soon */}
          <div className="mt-16 text-center">
            <div className="inline-block border border-[#C5A059] px-8 py-6">
              <p className="text-[#C5A059] uppercase tracking-widest text-sm mb-2">More Photos Coming Soon</p>
              <p className="text-gray-500 text-sm">We're constantly updating our gallery with new event setups</p>
            </div>
          </div>

          {/* Instagram CTA */}
          <div className="mt-16 text-center animate-fade-in-up">
            <a 
              href="https://www.instagram.com/vivahtents/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex flex-col items-center group"
              data-testid="gallery-instagram-link"
            >
              <Instagram size={48} className="text-[#C5A059] mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="font-heading text-2xl md:text-3xl text-[#1A1A1A] mb-2">
                Check out more photos and inspiration
              </h3>
              <p className="text-[#C5A059] text-lg font-semibold uppercase tracking-widest group-hover:underline">
                Follow us on Instagram @vivahtents
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-[#C5A059]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">Want a Setup Like This?</h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Let us create something beautiful for your next event. Get a free quote today.
          </p>
          <Link to="/quote" data-testid="gallery-cta-quote">
            <Button className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-none px-10 py-6 uppercase tracking-widest text-sm">
              Request a Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

// ==================== MAIN APP ====================
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Navbar />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/luxury-tents" element={<LuxuryTentsPage />} />
          <Route path="/services/tables-chairs" element={<TablesChairsPage />} />
          <Route path="/services/floor-wraps" element={<FloorWrapsPage />} />
          <Route path="/services/event-decor" element={<EventDecorPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/quote" element={<QuotePage />} />
        </Routes>
        <Footer />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
